import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login, formatApiErrorDetail } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/app/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const quick = (em) => { setEmail(em); setPassword(em.includes("nyathi") ? "Equity2026!" : "Password123!"); };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-nhs-ink text-white p-12">
                <Link to="/" className="flex items-center gap-2"><HeartHandshake className="h-6 w-6 text-nhs-teal" /><span className="font-heading font-extrabold text-lg">EquitySupport</span></Link>
                <div>
                    <h2 className="font-heading text-3xl font-bold leading-snug">Confidential support, when it matters most.</h2>
                    <p className="mt-4 text-slate-300 max-w-md">Sign in to access support cases, mediation and your Trust's equity insight.</p>
                </div>
                <p className="text-sm text-slate-400">GDPR compliant · NHS cyber aligned</p>
            </div>

            <div className="flex items-center justify-center p-6 bg-background">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-2 mb-8 justify-center"><HeartHandshake className="h-6 w-6 text-nhs-blue" /><span className="font-heading font-extrabold text-lg text-nhs-ink">EquitySupport</span></div>
                    <h1 className="font-heading text-2xl font-bold text-nhs-ink">Sign in</h1>
                    <p className="text-sm text-slate-500 mt-1">Enter your NHS credentials to continue.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@nhs.net" className="mt-1.5" data-testid="login-email-input" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="login-submit-button">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                        </Button>
                    </form>

                    <div className="mt-6 rounded-lg bg-white border border-slate-200 p-4 text-xs text-slate-500">
                        <p className="font-semibold text-nhs-ink mb-2">Demo accounts</p>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => quick("staff@equitysupport.nhs")} className="text-left hover:text-nhs-blue" data-testid="demo-staff">Staff → staff@equitysupport.nhs</button>
                            <button onClick={() => quick("hr@equitysupport.nhs")} className="text-left hover:text-nhs-blue" data-testid="demo-hr">HR / Leadership → hr@equitysupport.nhs</button>
                        </div>
                    </div>

                    <p className="mt-6 text-sm text-slate-500 text-center">No account? <Link to="/register" className="text-nhs-blue font-semibold" data-testid="to-register">Register</Link></p>
                </div>
            </div>
        </div>
    );
}
